import { buildPendingModel } from './pendingExportModel';

function fmt(n) {
  return Number(n).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Build the printable HTML for one company (mirrors the ExcelforClaude layout).
function buildHtml(model) {
  const { isGel, title, today, rows, sumTotal, headers } = model;

  const thStyle = 'border:1px solid #333;padding:6px 8px;background:#e5e7eb;font-size:12px;font-weight:bold;text-align:right;';
  const tdNum = 'border:1px solid #333;padding:5px 8px;font-size:12px;text-align:right;';
  const tdText = 'border:1px solid #333;padding:5px 8px;font-size:12px;';

  const headerCells = headers.map((h) => `<th style="${thStyle}">${esc(h)}</th>`).join('');

  const bodyRows = rows.map((r) => {
    const nums = isGel ? [r.qty, r.unit, r.value] : [r.qty, r.unit, r.value, r.vat, r.total];
    const tds = nums.map((v) => `<td style="${tdNum}">${fmt(v)}</td>`).join('');
    return `<tr><td style="${tdText}">${esc(r.label)}</td>${tds}</tr>`;
  }).join('');

  // Sum row — value only in the last (total) column, like the template.
  const emptyBefore = headers.length - 1;
  let sumCells = '';
  for (let i = 0; i < emptyBefore; i++) sumCells += `<td style="${tdNum}"></td>`;
  sumCells += `<td style="${tdNum}font-weight:bold;">${fmt(sumTotal)}</td>`;
  const sumRow = `<tr><td style="${tdText}"></td>${sumCells}</tr>`;

  return `
    <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:12px;">
      <div style="font-size:16px;font-weight:bold;">${esc(title)}</div>
      <div style="font-size:12px;color:#555;">${esc(today)}</div>
    </div>
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr><th style="${thStyle}text-align:left;"></th>${headerCells}</tr></thead>
      <tbody>${bodyRows}${sumRow}</tbody>
    </table>
  `;
}

/**
 * Build and download a PDF for one company's pending invoices.
 * Rendered via the browser (html2canvas) so Armenian/Cyrillic use system fonts
 * without embedding TTFs. Uses the same model as the Excel export.
 */
export async function exportPendingPdf(company, invoices) {
  const model = buildPendingModel(company, invoices);
  if (!model) return;

  const [{ jsPDF }, html2canvasMod] = await Promise.all([
    import('jspdf'),
    import('html2canvas'),
  ]);
  const html2canvas = html2canvasMod.default;

  const container = document.createElement('div');
  container.style.cssText =
    'position:fixed;left:-10000px;top:0;width:760px;background:#ffffff;padding:24px;' +
    "font-family:'Noto Sans Armenian','Segoe UI',Arial,'DejaVu Sans',sans-serif;color:#0f172a;";
  container.innerHTML = buildHtml(model);
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, { scale: 2, backgroundColor: '#ffffff' });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 24;
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW - margin * 2;
    const imgH = (canvas.height * imgW) / canvas.width;

    if (imgH <= pageH - margin * 2) {
      pdf.addImage(imgData, 'PNG', margin, margin, imgW, imgH);
    } else {
      // Content taller than one page — slice across pages.
      let heightLeft = imgH;
      let position = margin;
      pdf.addImage(imgData, 'PNG', margin, position, imgW, imgH);
      heightLeft -= pageH - margin * 2;
      while (heightLeft > 0) {
        position = margin - (imgH - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, position, imgW, imgH);
        heightLeft -= pageH - margin * 2;
      }
    }

    const filename = `${model.fileBase}_pending_${model.today}.pdf`;
    pdf.save(filename); // download
    // Also return the file as a Blob so callers can upload it (e.g. attach to a firm).
    return { blob: pdf.output('blob'), filename, isGel: model.isGel };
  } finally {
    document.body.removeChild(container);
  }
}
